import React, { useContext, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useParams } from 'react-router-dom'
import { Plus, Link45deg } from 'react-bootstrap-icons'
import { DraftContext } from '../../../../../../contexts/DraftContext'
import axiosService from '../../../../../../utils/AxiosService'
import { toastSuccess, toastError } from '../../../../../FormComponents/Toasts'
import InfoTooltip from '../../../../../FormComponents/InfoTooltip'
import Modal from '../../../../../Modal/Modal'
import RecipientInfluencerGroup from './RecipientInfluencerGroup'
import RecipientEmailsSection from './RecipientEmailsSection'
import ActiveLinksSection from './ActiveLinksSection'

const CLIENT_ELIGIBLE_STATUSES = ['internally_approved', 'client_review', 'approved']

export default observer(function SendRequestsModal({ isOpen, onClose }) {
  const { id } = useParams()
  const { influencers, actions, actionsForInfluencer, draftState } = useContext(DraftContext)

  const [messages, setMessages] = useState([''])
  const [activeMessage, setActiveMessage] = useState(0)
  const [manualEmailsByInfluencer, setManualEmailsByInfluencer] = useState({})
  const [password, setPassword] = useState('')
  const [sending, setSending] = useState(false)
  const [linksRefreshKey, setLinksRefreshKey] = useState(0)

  const selectedActions = actions.filter(a => draftState.isActionSelected(a.id))

  // One influencer per group - an influencer link/request can only ever
  // cover a single influencer's actions.
  const selectedGroups = influencers
    .map(influencer => ({ influencer, actions: actionsForInfluencer(influencer.id).filter(a => draftState.isActionSelected(a.id)) }))
    .filter(group => group.actions.length > 0)

  const clientEligible = selectedActions.some(a => CLIENT_ELIGIBLE_STATUSES.includes(a.action_draft?.draft_status))

  const addManualEmail = (influencerId, email) => {
    const existing = manualEmailsByInfluencer[influencerId] || []
    if (existing.includes(email)) return

    setManualEmailsByInfluencer({ ...manualEmailsByInfluencer, [influencerId]: [...existing, email] })
  }

  const removeManualEmail = (influencerId, email) => {
    setManualEmailsByInfluencer({
      ...manualEmailsByInfluencer,
      [influencerId]: (manualEmailsByInfluencer[influencerId] || []).filter(e => e !== email),
    })
  }

  const recipientEmailsFor = (influencer) => (
    [influencer.email, ...(manualEmailsByInfluencer[influencer.id] || [])].filter(Boolean)
  )

  const copyLink = (role) => {
    if (selectedActions.length === 0) return toastError('Select at least one action first')

    if (role === 'influencer' && selectedGroups.length > 1) {
      return toastError('Select actions for a single influencer to copy an influencer link')
    }

    const actionIds = role === 'influencer' ? selectedGroups[0].actions.map(a => a.id) : selectedActions.map(a => a.id)

    axiosService.post(`/api/project_manager/campaigns/${id}/draft_access_grants`, {
      draft_access_grant: { recipient_role: role, action_ids: actionIds, ...(password && { password }) },
    }).then(grant => {
      navigator.clipboard.writeText(`${window.location.origin}/drafts/${grant.token}`)
      toastSuccess(`${role === 'client' ? 'Client' : 'Influencer'} link copied to clipboard`)
      setLinksRefreshKey(key => key + 1)
    }).catch(({ data: { error } }) => toastError(error))
  }

  const sendRequests = () => {
    if (selectedGroups.length === 0) return toastError('Select at least one action first')

    const groupsWithoutEmail = selectedGroups.filter(group => recipientEmailsFor(group.influencer).length === 0)
    if (groupsWithoutEmail.length > 0) {
      return toastError(`Add a recipient email for ${groupsWithoutEmail.map(g => g.influencer.username).join(', ')}`)
    }

    setSending(true)
    Promise.all(selectedGroups.map(group => (
      axiosService.post(`/api/project_manager/campaigns/${id}/draft_requests`, {
        draft_request: {
          action_ids: group.actions.map(a => a.id),
          recipient_emails: recipientEmailsFor(group.influencer),
          message: messages[activeMessage],
        },
      })
    ))).then(() => {
      toastSuccess('Requests sent')
      onClose()
    }).catch(({ data: { error } }) => toastError(error))
      .finally(() => setSending(false))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header>Send Requests</Modal.Header>
      <Modal.Body>
        <div className="flex items-center gap-2 mb-4">
          {messages.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveMessage(index)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg ${
                activeMessage === index ? 'bg-rb-green text-white' : 'border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter dark:text-white'
              }`}
            >
              Message {index + 1}
            </button>
          ))}
          <button
            onClick={() => { setMessages([...messages, '']); setActiveMessage(messages.length) }}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium dark:text-white"
          >
            <Plus /> Add New
          </button>
        </div>

        <RecipientEmailsSection
          checkedGroups={selectedGroups}
          manualEmailsByInfluencer={manualEmailsByInfluencer}
          onAddEmail={addManualEmail}
          onRemoveEmail={removeManualEmail}
        />

        <p className="font-bold mb-2 dark:text-white">Message text</p>
        <textarea
          rows={4}
          value={messages[activeMessage]}
          onChange={e => setMessages(messages.map((m, i) => i === activeMessage ? e.target.value : m))}
          className="w-full p-2 mb-4 border-1 border-rb-lite-grey rounded-lg focus:ring-transparent focus:border-rb-green dark:bg-rb-dark-mode dark:text-white dark:border-rb-dark-mode-lighter"
        />

        <p className="font-bold mb-2 dark:text-white">Select Recipients</p>
        <div className="mb-4 max-h-72 overflow-y-auto">
          {influencers.map(inf => (
            <RecipientInfluencerGroup key={inf.id} influencer={inf} />
          ))}
        </div>

        <ActiveLinksSection refreshKey={linksRefreshKey} />

        <div className="flex items-center gap-1 mb-2">
          <p className="font-bold dark:text-white">Requests share link</p>
          <InfoTooltip text="Links are generated from the Actions currently selected above. An influencer link only covers one influencer at a time." />
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Add Password"
            className="flex-1 p-2 border-1 border-rb-lite-grey rounded-lg focus:ring-transparent focus:border-rb-green dark:bg-rb-dark-mode dark:text-white dark:border-rb-dark-mode-lighter"
          />
          <button
            disabled={!clientEligible}
            onClick={() => copyLink('client')}
            title={clientEligible ? undefined : 'No selected Actions are ready for client review yet'}
            className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter whitespace-nowrap ${
              clientEligible ? 'dark:text-white' : 'text-rb-mat-grey cursor-not-allowed opacity-50'
            }`}
          >
            <Link45deg /> Copy link for Client
          </button>
          <button
            onClick={() => copyLink('influencer')}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-white bg-rb-green rounded-lg whitespace-nowrap"
          >
            <Link45deg /> Copy link for Influencer
          </button>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button onClick={onClose} className="rb-btn-cancel px-4 py-2 text-sm font-medium">Cancel</button>
        <button disabled={sending} onClick={sendRequests} className="px-4 py-2 text-sm font-medium text-white bg-rb-green rounded-lg disabled:opacity-50">
          Send out Requests
        </button>
      </Modal.Footer>
    </Modal>
  )
})
